// The account of a person who signs in here: the e-mail code, the sessions that last 30 days per device, the first workspace.
import { z } from "zod";
import { IsoDateTime, Uuid, withoutLink } from "./common";
import { Country, Language, Timezone, UiLang, Vertical } from "./identity";
import { SignupSource } from "./signup-source";
import { SsoExchangeResponse as DirectSession } from "./sso";

const Email = z.string().trim().min(3).max(254).email();

export const SIGN_IN_CODE_LENGTH = 6;
export const SIGN_IN_TTL_MINUTES = 10;
export const SIGN_IN_MAX_ATTEMPTS = 5;
/** Wrong codes one address may type on one host in 24 hours, over all its letters. Then only the link works until the day has passed. */
export const SIGN_IN_WRONG_CODES_PER_DAY = 15;
/** How long a device stays signed in after it was last used. */
export const DEVICE_SESSION_DAYS = 30;

export const SignInCodeRequest = z.strictObject({
  email: Email,
  lang: UiLang.default("en"),
  /** The bot check's answer, from the widget on the page. Absent where the page shows no widget (`botCheckSiteKey` is null). */
  turnstileToken: z.string().trim().min(1).max(2048).optional(),
  /** s10-site-a: where the person came from, kept on the letter and written once if this sign-in makes the account. */
  source: SignupSource.optional(),
});
export type SignInCodeRequest = z.input<typeof SignInCodeRequest>;

/** The same answer for any address. Only the browser holding this id may type the code from the letter. */
export const SignInCodeAccepted = z.object({ signInId: z.string().regex(/^[0-9a-f]{32}$/) });
export type SignInCodeAccepted = z.infer<typeof SignInCodeAccepted>;

export const SignInCodeVerify = z.strictObject({
  signInId: SignInCodeAccepted.shape.signInId,
  code: z.string().trim().regex(/^\d{6}$/, "sign_in_code_format"),
});
export type SignInCodeVerify = z.input<typeof SignInCodeVerify>;

/** A refresh may say which workspace the tab is in, so each tab keeps its own. */
export const AccountRefreshRequest = z.strictObject({ tenantId: Uuid.optional(), lang: UiLang.optional() });
export type AccountRefreshRequest = z.input<typeof AccountRefreshRequest>;

export const AccountUserDto = z.object({ id: Uuid, name: z.string(), email: z.string().nullable() });

/**
 * Who signed in, and the workspace they are in, if any yet. `persistent` says whether the device keeps them signed in for 30 days through
 * a cookie the page cannot read; otherwise the token lasts the tab, as on a white-labeled host's domain.
 */
export const AccountSignedIn = z.object({
  token: z.string(),
  expiresAt: IsoDateTime,
  user: AccountUserDto,
  lang: UiLang,
  persistent: z.boolean(),
  /** Null until the first workspace exists. Its token is the same as the one above. */
  session: DirectSession.nullable(),
});
export type AccountSignedIn = z.infer<typeof AccountSignedIn>;

export const ACCOUNT_KINDS = ["business", "agency"] as const;

/** The first workspace of an account, made after signing in. */
export const WorkspaceRequest = z.strictObject({
  name: withoutLink(z.string().trim().min(1).max(200)),
  /** The person's own name, when the account still has only the address's. */
  personName: withoutLink(z.string().trim().min(1).max(120)).optional(),
  kind: z.enum(ACCOUNT_KINDS).default("business"),
  country: Country,
  vertical: Vertical,
  languages: z.array(Language).min(1).max(8).optional(),
  timezone: Timezone.optional(),
  /** Optional in the shape only, so a missing tick is refused with `terms_required` and not as a malformed body. */
  termsVersion: z.string().trim().max(32).optional(),
});
export type WorkspaceRequest = z.input<typeof WorkspaceRequest>;

export const AccountDeviceDto = z.object({
  id: Uuid,
  /** Browser and system, such as "Chrome · macOS". */
  userAgent: z.string(),
  ipPrefix: z.string().nullable(),
  createdAt: IsoDateTime,
  lastUsedAt: IsoDateTime,
  current: z.boolean(),
});
export type AccountDeviceDto = z.infer<typeof AccountDeviceDto>;

export const AccountDeviceList = z.object({ items: z.array(AccountDeviceDto) });
export type AccountDeviceList = z.infer<typeof AccountDeviceList>;

export const AccountSignedOut = z.object({ signedOut: z.number().int().min(0), current: z.boolean() });
export type AccountSignedOut = z.infer<typeof AccountSignedOut>;
